import { useContext, useEffect } from 'react';

import homeAndAutoAPI from '~/util/homeAndAutoAPI';
import setGraphAuthHeader from '~/util/apollo/setAuthHeader';
import { setSentryUserContext } from '~/util/sentry';
import { setRumGlobalContext } from '~/util/datadog_rum';
import loadMagicLinkWidget from '~/util/magicLink/load';
import loadSpousalModalWidget from '~/util/spousalModal/load';

import trackReferalProfile from '~/analytics/util/trackReferalProfile';
import frontendAnalyticsIdentify from '~/analytics/util/frontendAnalyticsIdentify';
import frontendAnalyticsPage from '~/analytics/util/frontendAnalyticsPage';

import addAuthHeader from '../../util/addAuthHeader';
import { AUTH_ERROR, UPDATE_USER } from '../../constants';
import { AuthContext } from '../..';
import { UUID_SESSION_STORAGE_KEY } from '../constants';

interface UserData {
  uuid: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  applicationId: string;
}

type GetUserData = (token: string) => Promise<UserData>;

const getUserData: GetUserData = async (token) => {
  const userRes = await homeAndAutoAPI.get('/me', {
    headers: addAuthHeader({}, token),
    params: new URLSearchParams(window.location.search),
  });

  return {
    email: userRes.data.email,
    uuid: userRes.data.uuid,
    lastName: userRes.data.last_name,
    firstName: userRes.data.first_name,
    applicationId: userRes.data.application_id,
  };
};

const trackUserAnalytics = (user: UserData, token: string) => {
  const queryParams = new URLSearchParams(window.location.search);
  const { uuid } = user;

  trackReferalProfile(token);
  frontendAnalyticsIdentify(uuid, {
    user_id: uuid,
    uuid,
    visitor_id: queryParams.get('visitor_id'),
    web_browser_uuid: queryParams.get('web_browser_uuid'),
  });

  frontendAnalyticsPage(window.location);

  window.traits = {
    user_id: uuid,
    uuid,
  };
};

const setUserDependencies = (user: UserData, token: string) => {
  const { uuid } = user;

  loadMagicLinkWidget(uuid);
  loadSpousalModalWidget(uuid, token);

  if (uuid) {
    setSentryUserContext(uuid);
    setRumGlobalContext(uuid);
  }

  sessionStorage.setItem(UUID_SESSION_STORAGE_KEY, uuid);
};

export const useGetUserData = () => {
  const {
    authDispatch,
    auth: { token },
  } = useContext(AuthContext);

  useEffect(() => {
    if (token) {
      setGraphAuthHeader(token);

      getUserData(token)
        .then((user) => {
          trackUserAnalytics(user, token);
          setUserDependencies(user, token);

          authDispatch({
            type: UPDATE_USER,
            payload: { user },
          });
        })
        .catch((err) => {
          const error = err.response?.data ? err.response.data.msg : err;

          authDispatch({ type: AUTH_ERROR, payload: { error } });
        });
    }
  }, [token, authDispatch]);
};
